import React from 'react'
import styled from 'styled-components'
import { ink, line, font } from 'features/dashboard/tokens'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'

const pctLabel = value => {
  const rounded = Math.round(value)
  return rounded === 0 && value > 0 ? '<1%' : `${rounded}%`
}

const CountDonut = ({ segments = [], size = 150, stroke = 20, centreValue, centreLabel, noun, onSegmentClick }) => {
  const things = noun || (centreLabel ? centreLabel.toLowerCase() : 'tasks')
  const shown = segments.filter(s => Number(s.count) > 0)
  const total = shown.reduce((sum, s) => sum + Number(s.count), 0)
  const r = (size - stroke) / 2
  const C = 2 * Math.PI * r

  if (total === 0) {
    return (
      <Wrap>
        <Ring style={{ width: size, height: size }}>
          <svg width={size} height={size} role='img' aria-label={`No ${things} in this period`}>
            <circle
              cx={size / 2} cy={size / 2} r={r}
              fill='none' stroke={line.base} strokeWidth={stroke}
              strokeDasharray='4 6' strokeLinecap='round'
            />
          </svg>
          <Centre>
            <CentreValue>0</CentreValue>
            <CentreLabel>{centreLabel || 'Tasks'}</CentreLabel>
          </Centre>
        </Ring>
      </Wrap>
    )
  }

  let offset = 0

  return (
    <Wrap>
      <Ring style={{ width: size, height: size }}>
        <svg width={size} height={size} role='img' aria-label={`${total} ${things} by progress`}>
          {shown.map(s => {
            const frac = Number(s.count) / total
            const dash = frac * C
            const circle = (
              <circle
                key={s.key}
                cx={size / 2} cy={size / 2} r={r}
                fill='none'
                stroke={s.tint}
                strokeWidth={stroke}
                strokeDasharray={`${dash} ${C - dash}`}
                strokeDashoffset={-offset}
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
              >
                <title>{`${s.label}: ${s.count} (${pctLabel(frac * 100)})`}</title>
              </circle>
            )
            offset += dash
            return circle
          })}
        </svg>
        <Centre>
          <CentreValue>{(centreValue ?? total).toLocaleString('en-GB')}</CentreValue>
          <CentreLabel>{centreLabel || 'Tasks'}</CentreLabel>
        </Centre>
      </Ring>

      <Legend>
        {shown.map(s => {
          const exact = (Number(s.count) / total) * 100
          const pct = Math.round(exact)
          const clickable = !!onSegmentClick && !!s.drill
          return (
            <LegendRow
              key={s.key}
              as={clickable ? 'button' : 'div'}
              type={clickable ? 'button' : undefined}
              $clickable={clickable}
              onClick={clickable ? () => onSegmentClick(s) : undefined}
              title={clickable ? `${s.help} Select to see them.` : s.help}
            >
              <Swatch style={{ background: s.tint }} />
              <LegendLabel $clickable={clickable}>{s.label}</LegendLabel>
              <LegendValue>{s.count.toLocaleString('en-GB')}</LegendValue>
              <LegendPct>{pctLabel(exact)}</LegendPct>
              <LegendGo aria-hidden='true'>{clickable ? <ChevronRightRoundedIcon style={{ fontSize: 18 }} /> : null}</LegendGo>
            </LegendRow>
          )
        })}
      </Legend>
    </Wrap>
  )
}

export default CountDonut

const Wrap = styled.div`
  display: flex; align-items: center; gap: 22px; flex-wrap: wrap;
  @media (max-width: 768px) { gap: 16px; justify-content: center; }
`
const Ring = styled.div`position: relative; flex-shrink: 0;`
const Centre = styled.div`
  position: absolute; inset: 0; display: flex; flex-direction: column;
  align-items: center; justify-content: center; pointer-events: none;
`
const CentreValue = styled.div`font-size: 26px; font-weight: 800; color: ${ink.base}; line-height: 1.1;`
const CentreLabel = styled.div`font-size: ${font.caption}; color: ${ink.mute}; margin-top: 2px;`
const Legend = styled.div`
  display: flex; flex-direction: column; gap: 2px;
  flex: 0 1 340px; min-width: 240px; max-width: 380px;
  @media (max-width: 768px) { min-width: 0; max-width: none; width: 100%; flex: 1 1 100%; }
`
const LegendRow = styled.div`
  display: grid; grid-template-columns: 12px minmax(0, 1fr) auto 40px 18px; align-items: center; gap: 9px;
  width: 100%; min-height: 40px; padding: 5px 7px; border-radius: 6px;
  border: 1px solid ${({ $clickable }) => ($clickable ? 'transparent' : 'transparent')};
  background: transparent; text-align: left; font-family: inherit;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'help')};
  &:hover {
    background: ${({ $clickable }) => ($clickable ? '#f0fbfc' : 'transparent')};
    border-color: ${({ $clickable, theme }) => ($clickable ? theme.colors?.secondary || '#33becc' : 'transparent')};
  }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.colors?.secondary || '#33becc'}; outline-offset: 1px; }
`
const Swatch = styled.span`width: 11px; height: 11px; border-radius: 3px; flex-shrink: 0;`
const LegendGo = styled.span`display: inline-flex; color: ${ink.mute};`
const LegendLabel = styled.span`
  font-size: 13px; color: ${ink.soft}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  ${LegendRow}:hover & { text-decoration: ${({ $clickable }) => ($clickable ? 'underline' : 'none')}; }
`
const LegendValue = styled.span`font-size: 13px; font-weight: 700; color: ${ink.base}; font-variant-numeric: tabular-nums;`
const LegendPct = styled.span`font-size: 13px; color: ${ink.mute}; text-align: right; font-variant-numeric: tabular-nums;`
